import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import RowList from "../../../components/common/rowList";
import InfoPopover from "../../../components/common/infoPopover";
import useDarkMode from "../../../hooks/useDarkMode";
import { AppCard } from "../../../components/common/appShell";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
);

const ChartLoadingPanel = ({ dark }) => (
  <div
    role="status"
    aria-live="polite"
    style={{
      minHeight: "280px",
      border: `1px solid ${dark ? "rgba(148, 163, 184, 0.2)" : "#e5e7eb"}`,
      borderRadius: "6px",
      background: dark ? "rgba(15, 23, 42, 0.28)" : "#f8fafc",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "14px",
      padding: "28px",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
      <div className="skeleton-blank" style={{ width: "180px", height: "16px", margin: 0 }} />
      <div className="skeleton-blank" style={{ width: "120px", height: "16px", margin: 0 }} />
    </div>
    <div className="skeleton-blank" style={{ width: "100%", height: "136px", margin: 0 }} />
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      <div className="skeleton-blank" style={{ width: "34%", height: "12px", margin: 0 }} />
      <div className="skeleton-blank" style={{ width: "24%", height: "12px", margin: 0 }} />
    </div>
    <span style={{ color: "var(--app-text-muted)", fontSize: "13px" }}>
      Loading power history...
    </span>
  </div>
);

const INLET_METRIC_CONFIGS = {
  currentRms: {
    metricKey: "totalCurrentRms",
    metricLabel: "Current RMS",
    optionLabel: "Current RMS",
    title: "Inlet Current RMS",
    unit: "A",
    color: "#2563eb",
  },
  voltageRms: {
    metricKey: "avgVoltageRms",
    metricLabel: "Voltage RMS",
    optionLabel: "Voltage RMS",
    title: "Inlet Voltage RMS",
    unit: "V",
    color: "#f97316",
  },
};

const INLET_SUMMARY_METRICS = [
  { key: "inletCurrentRms",          label: "Current RMS",        unit: "A",   decimals: 2, color: "#2563eb" },
  { key: "inletActivePower",         label: "Active Power",       unit: "W",   decimals: 2, color: "#10b981" },
  { key: "inletEnergyAccumulation",  label: "Energy Consumption", unit: "kWh", decimals: 2, color: "#f59e0b" },
];

const ATS_INLET_IDS = ["A", "B"];

/**
 * DevicePageInletEnhanced – single‑inlet drill‑down view (inline‑style version)
 * ---------------------------------------------------------------------------
 * •  Headline StatCards highlight Current RMS, Active Power, kWh.
 * •  Works gracefully when only one metric exists (RMS‑only PDUs).
 * •  All visual tweaks use inline `style` props—no external CSS required.
 */
export default function DevicePageInletEnhanced({ inlets = [], banks = [], loading, device }) {
  const isATSDevice = device?.inletConfig === "ats";
  const prioritizedInlet = useMemo(() => {
    if (!isATSDevice) return inlets[0];
    return (
      inlets.find((candidate) => candidate?.atsInletActive) ||
      inlets.find((candidate) => candidate?.atsInletReady) ||
      inlets[0]
    );
  }, [inlets, isATSDevice]);
  const inlet = prioritizedInlet || {}; // focus on active inlet when ATS
  const serialNumber = device?.enclosureSerialNumber || inlet.enclosureSerialNumber;
  const inletIdentifier = (() => {
    if (!inlet?.id) return "";
    const baseName = inlet.inletName || `Inlet ${inlet.id}`;
    const nameWithId = inlet.inletName ? `${baseName} (${inlet.id})` : baseName;
    if (device?.deviceName) {
      return `${device.deviceName} • ${nameWithId}`;
    }
    return nameWithId;
  })();
  const inletEnergyCapable = device?.inletEnergySupported === true;
  const inletCurrentFlag = device?.inletCurrentSupported;

  const supportsCurrentLogs = Boolean(
    serialNumber &&
    inlet?.id &&
    (inletEnergyCapable || inletCurrentFlag !== false)
  );

  const supportsVoltageLogs = Boolean(
    serialNumber &&
    inlet?.id &&
    inletEnergyCapable
  );

  const availableMetricKeys = useMemo(() => {
    const keys = [];
    if (supportsCurrentLogs) keys.push("currentRms");
    if (supportsVoltageLogs) keys.push("voltageRms");
    return keys;
  }, [supportsCurrentLogs, supportsVoltageLogs]);

  const [selectedMetricKey, setSelectedMetricKey] = useState(() => availableMetricKeys[0] || "currentRms");

  const atsStatusRows = useMemo(() => {
    if (!isATSDevice) return [];
    return ATS_INLET_IDS.map((atsId) => {
      const matchingInlet = inlets.find((candidate) => candidate?.atsInletId === atsId);
      return {
        label: `ATS Inlet ${atsId}`,
        value: formatAtsInletStatus(matchingInlet),
      };
    });
  }, [inlets, isATSDevice]);

  useEffect(() => {
    const fallbackKey = availableMetricKeys[0] || "currentRms";
    if (!availableMetricKeys.includes(selectedMetricKey) && selectedMetricKey !== fallbackKey) {
      setSelectedMetricKey(fallbackKey);
    }
  }, [availableMetricKeys, selectedMetricKey]);

  useEffect(() => {
    if (availableMetricKeys.length === 0) return;
    const defaultKey = availableMetricKeys[0] || "currentRms";
    setSelectedMetricKey((current) => (current === defaultKey ? current : defaultKey));
  }, [inlet?.id, availableMetricKeys]);

  const activeMetricKey = availableMetricKeys.includes(selectedMetricKey)
    ? selectedMetricKey
    : availableMetricKeys[0];

  const activeMetric = INLET_METRIC_CONFIGS[activeMetricKey] || INLET_METRIC_CONFIGS.currentRms;

  /* ── headline metric config ─────────────────────────────────────────── */
  const headlineMetrics = INLET_SUMMARY_METRICS
    .map((metric) => {
      const rawValue = inlet?.[metric.key];
      if (rawValue === undefined || rawValue === null) return null;

      const numericValue = Number(rawValue);
      const formattedValue = Number.isFinite(numericValue)
        ? numericValue.toFixed(metric.decimals)
        : String(rawValue);

      return { ...metric, value: formattedValue };
    })
    .filter(Boolean);

  const dark = useDarkMode();

  if (loading) return <InletSkeleton />;

  /* ── render ─────────────────────────────────────────────────────────── */
  return (
    <div className="grid-container fluid">
      {/* Headline summary tiles */}
      {headlineMetrics.length > 0 && (
        <div style={SUMMARY_GRID_STYLE} className="device-inlet-summary-grid">
          {headlineMetrics.map(({ key, label, value, unit, color }) => (
            <SummaryTile key={key} label={label} value={value} unit={unit} color={color} dark={dark} />
          ))}
        </div>
      )}

      {serialNumber && inlet?.id && availableMetricKeys.length > 0 && activeMetric && (
        <div className="grid-x grid-padding-x grid-padding-y">
          <div className="cell large-12">
            <DeviceInletTrendPanel
              activeMetricKey={activeMetricKey}
              availableMetricKeys={availableMetricKeys}
              inlet={inlet}
              inletIdentifier={inletIdentifier}
              onMetricChange={setSelectedMetricKey}
              serialNumber={serialNumber}
            />
          </div>
        </div>
      )}

      {/* Detail lists */}
      <div className="grid-x grid-padding-y grid-padding-x">
        {/* Inlet stats */}
        <div className="cell large-6">
          <AppCard
            title="Inlet statistics"
            description="Review live electrical readings and accumulated energy for the active inlet."
          >
          <RowList
            rows={[
              { label: "Inlet ID",              value: inlet.id },
              { label: "Current RMS",           value: <DataValue value={inlet.inletCurrentRms} unit="A" /> },
              { label: "Voltage RMS",           value: <DataValue value={inlet.inletVoltageRms} unit="V" /> },
              { label: "Line Frequency",        value: <DataValue value={inlet.inletLineFrequency} unit="Hz" /> },
              { label: "Power Factor",          value: <DataValue value={inlet.inletPowerFactor} unit="" /> },
              { label: "Energy Consumption",    value: <DataValue value={inlet.inletEnergyAccumulation} unit="kWh" /> },
              { label: "Active Power",          value: <DataValue value={inlet.inletActivePower} unit="W" /> },
              { label: "Apparent Power",        value: <DataValue value={inlet.inletApparentPower} unit="VA" /> },
            ]}
          />
          </AppCard>
        </div>

        {/* Meta / static info */}
        <div className="cell large-6">
          <AppCard
            title="Inlet information"
            description="Review serial, inlet type, phase details, and ATS status for this device."
          >
          <RowList
            rows={[
              { label: "Enclosure Serial Number", value: inlet.enclosureSerialNumber },
              { label: "Inlet Plug",               value: inlet.inletPlug },
              { label: "Inlet Type",               value: inlet.inletType === "dual" ? "Dual Circuit" : inlet.inletType === "ats" ? "ATS" : "Standard" },
              { label: "Inlet Phase",              value: inlet.inletPhase },
              { label: "Connection State",         value: inlet.connectionState },
              ...atsStatusRows,
            ]}
          />
          </AppCard>
        </div>
      </div>
    </div>
  );
}

const InletSkeleton = () => {
  const dark = useDarkMode();

  const skeletonLine = (
    <div
      className="skeleton-blank"
      style={{ width: "120px", height: "14px", margin: 0, padding: 0 }}
    />
  );

  return (
    <div className="grid-container fluid">
      <div style={SUMMARY_GRID_STYLE} className="device-inlet-summary-grid">
        {INLET_SUMMARY_METRICS.map((metric) => (
          <div key={metric.key} className="skeleton-blank" style={SUMMARY_SKELETON_TILE_STYLE.tile(dark)}>
            <div style={{ ...SUMMARY_TILE_STYLE.accent, background: metric.color, width: "100%" }} />
          </div>
        ))}
      </div>
      <div className="grid-x grid-padding-y grid-padding-x">
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Stats", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Inlet ID", value: skeletonLine },
              { label: "Current RMS", value: skeletonLine },
              { label: "Voltage RMS", value: skeletonLine },
              { label: "Line Frequency", value: skeletonLine },
              { label: "Power Factor", value: skeletonLine },
              { label: "Energy Consumption", value: skeletonLine },
              { label: "Active Power", value: skeletonLine },
              { label: "Apparent Power", value: skeletonLine },
            ]}
          />
        </div>
        <div className="cell large-6">
          <RowList
            rows={[
              { label: "Inlet Information", labelStyle: { fontWeight: "bold", fontSize: "110%" } },
              { label: "Enclosure Serial Number", value: skeletonLine },
              { label: "Inlet Plug", value: skeletonLine },
              { label: "Inlet Type", value: skeletonLine },
              { label: "Inlet Phase", value: skeletonLine },
              { label: "Connection State", value: skeletonLine },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const TREND_RANGE_OPTIONS = [
  { key: "24h", label: "24 hours", durationMs: 24 * 60 * 60 * 1000, bucketMinutes: 15 },
  { key: "7d", label: "7 days", durationMs: 7 * 24 * 60 * 60 * 1000, bucketMinutes: 120 },
  { key: "30d", label: "30 days", durationMs: 30 * 24 * 60 * 60 * 1000, bucketMinutes: 720 },
];

const TREND_TIME_DISPLAY_FORMATS = {
  minute: "MMM d, h:mm a",
  hour: "MMM d, h a",
  day: "MMM d",
  month: "MMM yyyy",
};

const buildTrendRange = (rangeKey) => {
  const option = TREND_RANGE_OPTIONS.find((item) => item.key === rangeKey) || TREND_RANGE_OPTIONS[0];
  const before = Date.now();
  return {
    since: before - option.durationMs,
    before,
    bucketMinutes: option.bucketMinutes,
  };
};

const hasNumber = (value) =>
  value !== undefined && value !== null && Number.isFinite(Number(value));

const formatNumber = (value, digits = 2) => {
  const number = Number(value || 0);
  return number
    .toFixed(digits)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatTrendMetric = (value, metric) => {
  if (!hasNumber(value)) return "-";
  return `${formatNumber(value, metric.digits || 2)}${metric.unit ? ` ${metric.unit}` : ""}`;
};

const formatTrendPercent = (value) => {
  if (!hasNumber(value)) return "-";
  const number = Number(value);
  const sign = number > 0 ? "+" : "";
  return `${sign}${formatNumber(number, 1)}%`;
};

const formatBucketDuration = (minutes) => {
  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) return "latest";
  if (numericMinutes < 60) return `${numericMinutes} min`;
  const hours = numericMinutes / 60;
  return Number.isInteger(hours) ? `${hours} hr` : `${formatNumber(hours, 1)} hr`;
};

const resolveTrendTimeUnit = (durationMs) => {
  const hours = durationMs / 3600000;
  if (!Number.isFinite(hours) || hours <= 0) return "hour";
  if (hours <= 36) return "hour";
  if (hours <= 24 * 75) return "day";
  return "month";
};

const withAlpha = (color, alpha) => {
  if (!color || !color.startsWith("#")) return `rgba(37, 99, 235, ${alpha})`;
  const hex = color.slice(1);
  if (hex.length !== 6) return color;
  const value = parseInt(hex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatTrendDateRange = (since, before) => {
  const options = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return `${new Date(since).toLocaleString(undefined, options)} - ${new Date(before).toLocaleString(undefined, options)}`;
};

const expectedLoadLabel = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "Learning";
  if (anomaly.state === "near_expected") return "Near expected";
  if (anomaly.state === "low_load") return "Low load";
  if (anomaly.state === "high_load") return "High load";
  if (anomaly.state === "normal") return "Normal";
  return "Learning";
};

const expectedLoadColor = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "var(--app-text-muted)";
  if (anomaly.state === "normal") return "#047857";
  if (anomaly.state === "near_expected") return "var(--app-text-muted)";
  if (anomaly.severity === "critical") return "#b91c1c";
  if (anomaly.state === "low_load" || anomaly.state === "high_load") return "#b45309";
  return "var(--app-text-muted)";
};

const expectedRangeSubValue = (expected = {}) => {
  if (
    expected.status === "ready" &&
    hasNumber(expected.currentP10) &&
    hasNumber(expected.currentP90)
  ) {
    return `Expected ${formatNumber(expected.currentP10, 2)}-${formatNumber(expected.currentP90, 2)} A`;
  }
  if (expected.status === "too_many_channels") return "Too many channels";
  if (expected.status === "no_channels") return "No channels scoped";
  return "Need more history";
};

const expectedRangeInfo = (expected = {}, anomaly = {}, latestCurrent = null) => {
  if (expected.status !== "ready") {
    return (
      <div style={{ lineHeight: 1.45 }}>
        <div>Expected range needs more profile history.</div>
        <div style={{ marginTop: "0.65rem" }}>
          Learned from the last 30 days for the same UTC hour of week.
        </div>
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.45 }}>
      <div>Expected range: {formatNumber(expected.currentP10, 2)}-{formatNumber(expected.currentP90, 2)} A</div>
      <div>Expected median: {formatNumber(expected.currentMedian, 2)} A</div>
      <div>Latest current: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      {anomaly.durationMinutes > 0 ? (
        <div>Duration: {formatBucketDuration(anomaly.durationMinutes)}</div>
      ) : null}
      {anomaly.message ? (
        <div style={{ marginTop: "0.65rem" }}>{anomaly.message}</div>
      ) : null}
      <div style={{ marginTop: "0.65rem" }}>
        Learned from the last 30 days for the same UTC hour of week.
      </div>
    </div>
  );
};

const buildExpectedPointSet = (rows, field) =>
  rows
    .map((row) => {
      const x = row.createdAt ? new Date(row.createdAt) : null;
      if (!x || Number.isNaN(x.getTime())) return null;
      return {
        x,
        y: hasNumber(row[field]) ? Number(row[field]) : null,
      };
    })
    .filter(Boolean);

const buildExpectedCurrentDatasets = (rows) => {
  const hasExpectedRange = rows.some(
    (row) => hasNumber(row.expectedCurrentP10) && hasNumber(row.expectedCurrentP90),
  );
  if (!hasExpectedRange) return [];

  const rangeColor = "#64748b";
  return [
    {
      label: "Expected lower",
      data: buildExpectedPointSet(rows, "expectedCurrentP10"),
      borderColor: "rgba(100, 116, 139, 0)",
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 0,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
      hideFromLegend: true,
    },
    {
      label: "Expected range",
      data: buildExpectedPointSet(rows, "expectedCurrentP90"),
      borderColor: withAlpha(rangeColor, 0.35),
      backgroundColor: withAlpha(rangeColor, 0.14),
      fill: "-1",
      pointRadius: 0,
      borderWidth: 1,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
    {
      label: "Expected median",
      data: buildExpectedPointSet(rows, "expectedCurrentMedian"),
      borderColor: withAlpha(rangeColor, 0.8),
      borderDash: [4, 4],
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
  ];
};

function DeviceInletTrendPanel({
  activeMetricKey,
  availableMetricKeys,
  inlet,
  inletIdentifier,
  onMetricChange,
  serialNumber,
}) {
  const dark = useDarkMode();
  const [rangeKey, setRangeKey] = useState("24h");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [refreshIndex, setRefreshIndex] = useState(0);
  const activeMetric = INLET_METRIC_CONFIGS[activeMetricKey] || INLET_METRIC_CONFIGS.currentRms;
  const fallbackRange = buildTrendRange(rangeKey);
  const parsedSince = Date.parse(meta.since);
  const parsedBefore = Date.parse(meta.before);
  const since = Number.isFinite(parsedSince) ? parsedSince : fallbackRange.since;
  const before = Number.isFinite(parsedBefore) ? parsedBefore : fallbackRange.before;
  const durationMs = Math.max(1, before - since);
  const latest = meta.latest || {};
  const bucketMinutes = Number(meta.bucketMinutes || fallbackRange.bucketMinutes);
  const baselineCurrent = meta.baseline && hasNumber(meta.baseline.totalCurrentRms)
    ? Number(meta.baseline.totalCurrentRms)
    : null;
  const latestCurrent = hasNumber(latest.totalCurrentRms) ? Number(latest.totalCurrentRms) : null;
  const peakActivePower = rows.reduce((peak, row) => {
    const activePower = hasNumber(row.totalActivePower) ? Number(row.totalActivePower) : null;
    if (activePower === null) return peak;
    return peak === null ? activePower : Math.max(peak, activePower);
  }, null);
  const currentChange = hasNumber(meta.currentChangePct) ? Number(meta.currentChangePct) : null;
  const expected = meta.expected || {};
  const anomaly = meta.anomaly || {};
  const expectedDatasets = activeMetricKey === "currentRms" ? buildExpectedCurrentDatasets(rows) : [];
  const changeColor = currentChange === null
    ? "var(--app-text-muted)"
    : currentChange < -20
      ? "#b91c1c"
      : currentChange < 0
        ? "#b45309"
        : "#047857";
  const loadChangeDirection = currentChange === null
    ? "Not enough current data is available for this timeframe."
    : currentChange < 0
      ? "Current is lower than the earlier average for this timeframe."
      : currentChange > 0
        ? "Current is higher than the earlier average for this timeframe."
        : "Current matches the earlier average for this timeframe.";
  const loadChangeInfo = (
    <div style={{ lineHeight: 1.45 }}>
      <div>Latest {formatBucketDuration(bucketMinutes)} avg: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      <div>Earlier avg: {baselineCurrent !== null ? `${formatNumber(baselineCurrent, 2)} A` : "-"}</div>
      <div>Change: {formatTrendPercent(meta.currentChangePct)}</div>
      <div style={{ marginTop: "0.65rem" }}>{loadChangeDirection}</div>
    </div>
  );

  useEffect(() => {
    if (!serialNumber || !inlet?.id) {
      setRows([]);
      setMeta({});
      setLoaded(true);
      return undefined;
    }

    const range = buildTrendRange(rangeKey);
    const params = new URLSearchParams();
    params.set("enclosure_serial_number", String(serialNumber));
    params.set("inlet_id", String(inlet.id));
    if (inlet.inletIndex !== undefined || inlet.inlet_index !== undefined) {
      params.set("inlet_index", String(inlet.inletIndex ?? inlet.inlet_index));
    }
    params.set("since", String(range.since));
    params.set("before", String(range.before));
    params.set("bucketMinutes", String(range.bucketMinutes));

    const cancelSource = axios.CancelToken.source();
    setLoading(true);
    setError("");

    axios
      .get(`/v2/inlets/trends?${params.toString()}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        cancelToken: cancelSource.token,
      })
      .then((res) => {
        setRows(res.data?.data || []);
        setMeta(res.data?.meta || {});
        setLoaded(true);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.log(err);
        setRows([]);
        setMeta({});
        setLoaded(true);
        setError("Unable to load inlet trends.");
      })
      .finally(() => {
        setLoading(false);
      });

    return () => cancelSource.cancel("Canceling inlet trend request");
  }, [serialNumber, inlet?.id, inlet?.inletIndex, inlet?.inlet_index, rangeKey, refreshIndex]);

  const chartData = {
    datasets: [
      ...expectedDatasets,
      {
        label: activeMetric.metricLabel,
        data: rows
          .map((row) => {
            const x = row.createdAt ? new Date(row.createdAt) : null;
            if (!x || Number.isNaN(x.getTime())) return null;
            return {
              x,
              y: hasNumber(row[activeMetric.metricKey])
                ? Number(row[activeMetric.metricKey])
                : null,
            };
          })
          .filter(Boolean),
        borderColor: activeMetric.color,
        backgroundColor: withAlpha(activeMetric.color, 0.16),
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.18,
        spanGaps: false,
        parsing: false,
      },
    ],
  };

  const chartTheme = {
    axis: dark ? "#a8b3c2" : "#64748b",
    grid: dark ? "rgba(148, 163, 184, 0.14)" : "rgba(15, 23, 42, 0.08)",
    tooltipBg: dark ? "rgba(15, 23, 42, 0.96)" : "rgba(15, 23, 42, 0.92)",
    tooltipText: "#f8fafc",
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: chartTheme.axis,
          filter: (item, data) => !data.datasets[item.datasetIndex]?.hideFromLegend,
        },
      },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg,
        bodyColor: chartTheme.tooltipText,
        titleColor: chartTheme.tooltipText,
        borderColor: chartTheme.grid,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatTrendMetric(context.parsed?.y, activeMetric)}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        min: since,
        max: before,
        time: {
          tooltipFormat: "PP p",
          unit: resolveTrendTimeUnit(durationMs),
          displayFormats: TREND_TIME_DISPLAY_FORMATS,
        },
        ticks: { autoSkip: true, color: chartTheme.axis, maxRotation: 0 },
        grid: { color: chartTheme.grid, display: false },
      },
      y: {
        ticks: {
          color: chartTheme.axis,
          callback: (value) => formatTrendMetric(value, activeMetric),
        },
        grid: { color: chartTheme.grid },
      },
    },
  };

  const selectStyle = {
    margin: 0,
    width: "100%",
    padding: "0.35rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid var(--app-control-border)",
    backgroundColor: "var(--app-control-bg)",
    color: "var(--app-control-text)",
  };

  const controlLabelStyle = {
    color: "var(--text-muted-color, #6b7280)",
    display: "flex",
    flexDirection: "column",
    fontSize: "0.78rem",
    fontWeight: 700,
    gap: "0.35rem",
  };

  return (
    <AppCard title={activeMetric.title} description={inletIdentifier}>
      <div className="app-card__section" style={{ paddingTop: 0 }}>
        <div
          style={{
            alignItems: "end",
            display: "grid",
            gap: "0.85rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            marginBottom: "1rem",
          }}
        >
          <label style={controlLabelStyle}>
            <span>Metric</span>
            <select
              value={activeMetricKey}
              onChange={(event) => onMetricChange(event.target.value)}
              style={selectStyle}
            >
              {availableMetricKeys.map((key) => {
                const config = INLET_METRIC_CONFIGS[key];
                return (
                  <option key={key} value={key}>
                    {config?.optionLabel || key}
                  </option>
                );
              })}
            </select>
          </label>
          <label style={controlLabelStyle}>
            <span>Timeframe</span>
            <select
              value={rangeKey}
              onChange={(event) => setRangeKey(event.target.value)}
              style={selectStyle}
            >
              {TREND_RANGE_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button secondary app-toolbar__action-button"
            onClick={() => setRefreshIndex((current) => current + 1)}
            style={{
              justifyContent: "center",
              margin: 0,
              minHeight: "40px",
              width: "100%",
            }}
          >
            <i className="material-icons" style={{ fontSize: "18px" }}>refresh</i>
            <span>Refresh</span>
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gap: "1rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            marginBottom: "1rem",
          }}
        >
          <TrendStat
            label="Current now"
            value={formatTrendMetric(latest.totalCurrentRms, INLET_METRIC_CONFIGS.currentRms)}
          />
          <TrendStat
            label="Load change"
            value={formatTrendPercent(meta.currentChangePct)}
            color={changeColor}
            subValue={
              meta.loadDropDetected
                ? "Drop detected"
                : baselineCurrent !== null
                  ? `Baseline ${formatNumber(baselineCurrent, 2)} A`
                  : "Vs baseline"
            }
            info={loadChangeInfo}
          />
          <TrendStat
            label="Load state"
            value={expectedLoadLabel(expected, anomaly)}
            color={expectedLoadColor(expected, anomaly)}
            subValue={expectedRangeSubValue(expected)}
            info={expectedRangeInfo(expected, anomaly, latestCurrent)}
          />
          <TrendStat
            label="Energy used"
            value={hasNumber(meta.measuredEnergyDeltaKwh) ? `${formatNumber(meta.measuredEnergyDeltaKwh, 2)} kWh` : "-"}
            subValue={hasNumber(meta.measuredEnergyDeltaKwh) ? "From PDU kWh counter" : "No kWh counter data"}
            info="Measured from the PDU's cumulative kWh counter over this selected timeframe."
          />
          <TrendStat
            label="Peak active power"
            value={hasNumber(peakActivePower) ? `${formatNumber(peakActivePower, 0)} W` : "-"}
            subValue="Highest bucket total"
            info="Highest total active power observed in one bucket during this selected timeframe."
          />
        </div>
        <div
          style={{
            color: "var(--app-text-muted)",
            display: "flex",
            flexWrap: "wrap",
            fontSize: "0.85rem",
            gap: "1rem",
            justifyContent: "space-between",
            marginBottom: "0.75rem",
          }}
        >
          <span>{formatTrendDateRange(since, before)}</span>
          <span>{bucketMinutes} minute buckets</span>
        </div>
        <div style={{ minHeight: "280px", position: "relative" }}>
          {loading ? (
            <ChartLoadingPanel dark={dark} />
          ) : (
            <Line options={chartOptions} data={chartData} />
          )}
          {!loading && loaded && !error && rows.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                alignItems: "center",
                color: "var(--app-text-muted)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No trend data for this range.
            </div>
          )}
        </div>
        {error && (
          <div style={{ color: "#b91c1c", fontSize: "0.9rem", marginTop: "0.75rem" }}>
            {error}
          </div>
        )}
      </div>
    </AppCard>
  );
}

const TrendStat = ({ label, value, subValue, color = "var(--app-text)", info }) => (
  <div style={{ borderTop: "1px solid var(--app-border)", paddingTop: "0.75rem" }}>
    <div
      style={{
        alignItems: "center",
        color: "var(--app-text-muted)",
        display: "flex",
        fontSize: "0.78rem",
        fontWeight: 600,
        gap: "0.15rem",
      }}
    >
      <span>{label}</span>
      {info ? <InfoPopover text={info} /> : null}
    </div>
    <div style={{ color, fontWeight: 700, fontSize: "1.2rem", marginTop: "0.25rem" }}>
      {value}
    </div>
    {subValue && (
      <div style={{ color: "var(--app-text-muted)", fontSize: "0.82rem", marginTop: "0.2rem" }}>
        {subValue}
      </div>
    )}
  </div>
);

/* ── helpers ───────────────────────────────────────────────────────────── */
const SummaryTile = ({ label, value, unit, color, dark }) => {
  return (
    <div style={SUMMARY_TILE_STYLE.wrap(dark)} className="devices-summary-tile">
      <div style={SUMMARY_TILE_STYLE.big(dark)}>
        <span>{value}</span>
        {unit ? <span style={SUMMARY_TILE_STYLE.unit(dark)}>{unit}</span> : null}
      </div>
      <div style={SUMMARY_TILE_STYLE.label(dark)}>{label}</div>
      <div style={{ ...SUMMARY_TILE_STYLE.accent, background: color, width: "100%" }} />
    </div>
  );
};

const DataValue = ({ value, unit }) => {
  return (
    <span style={{ display: "flex", alignItems: "flex-end" }}>
      <span
        style={{
          fontWeight: "600",
          fontSize: "105%",
        }}
      >
        {(value === undefined) ? (
          "-"
        ) : (
          (value || 0).toFixed(2)
        )}
      </span>
      <span
        style={{
          fontSize: "80%",
          paddingLeft: "4px",
          paddingBottom: "2px",
        }}
      >
        {unit}
      </span>
    </span>
  );
};

const formatAtsInletStatus = (inlet) => {
  let statusLabel = "-";
  if (inlet) {
    if (inlet.atsInletActive) statusLabel = "Active";
    else if (inlet.atsInletReady) statusLabel = "Ready";
    else statusLabel = "Not Ready";
  }

  const color =
    statusLabel === "Not Ready"
      ? "maroon"
      : (statusLabel === "Active" || statusLabel === "Ready")
        ? "forestgreen"
        : undefined;

  return (
    <span style={{ fontWeight: 600, color }}>
      {statusLabel}
    </span>
  );
};

const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  gap: 16,
  marginBottom: "24px",
  marginTop: "20px"
};

const SUMMARY_TILE_STYLE = {
  wrap: (dark) => ({
    background: dark ? "var(--app-surface-1)" : "#fff",
    borderRadius: 3,
    padding: "20px 16px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    position: "relative",
    minHeight: 92,
  }),
  big: (dark) => ({
    fontSize: 28,
    fontWeight: 600,
    lineHeight: 1,
    color: dark ? "#f3f3f3" : "#2c3e50",
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  }),
  unit: (dark) => ({
    fontSize: 14,
    color: dark ? "var(--app-text-muted)" : "#6b7280",
    fontWeight: 500,
  }),
  label: (dark) => ({
    fontSize: 13,
    marginTop: 6,
    color: dark ? "#ccc" : "#7f8c8d",
  }),
  accent: {
    position: "absolute",
    left: 0,
    bottom: 0,
    height: 4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    transition: "width .4s ease",
  },
};

const SUMMARY_SKELETON_TILE_STYLE = {
  tile: (dark) => ({
    ...SUMMARY_TILE_STYLE.wrap(dark),
    background: dark ? "#555" : "rgb(230,230,230)",
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
    boxShadow: "none",
  }),
};
